import type { AuditLogState, DealHistory, StateTree } from '@makemydeal/dr-dash-types';
import type { AuditLogDealInformation, IDetailedAuditLog, RequestPdfAuditLogPayload } from '@makemydeal/dr-platform-types';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { createSelector } from 'reselect';
import { getDealHistoryIsLoading, getDealHistoryOffers } from './dealHistorySelectors';
import {
    getAnnualMiles,
    getCoBuyerInfo,
    getCurrentOfferType,
    getDealXgId,
    getDownPaymentByOfferType,
    getOfferPriceByOfferType,
    getSelectedTerm,
    getShopperInfo
} from './offerRedux';
import { getTotalAllowance } from './offerSelectors';
import { getVehicle } from './vehicleSelectors';
import { dateUtils } from '@makemydeal/dr-common-utils';

export const getDealAuditLog = (state: StateTree): AuditLogState => {
    return state.dealAuditLog || {};
};

export const getDealChangeLog = (state: StateTree): IDetailedAuditLog[] => {
    const dealChangeLog = getDealAuditLog(state);
    return dealChangeLog.auditLogData || [];
};

export const getChangeLogLoadingStatus = (state: StateTree): boolean => {
    return getDealAuditLog(state).isLoading;
};

export const getDownloadingVersion = (state: StateTree): string => {
    return getDealAuditLog(state).downloadingVersion || '';
};
export const getPrintErrorStatus = (state: StateTree): boolean => {
    return getDealAuditLog(state).printError || false;
};
export const getDealVersionLoadingStatus = (state: StateTree): boolean => getDealHistoryIsLoading(state);
export const getTransformedDealVersions = createSelector([getDealHistoryOffers], (offers) => {
    if (!offers) return [];
    return transformDealVersions(offers);
});

export const transformDealVersions = (data: DealHistory[]): AuditLogDealInformation[] => {
    return data.map((dealVersion: DealHistory, index: number) => {
        const offerType = getCurrentOfferType(dealVersion);
        const selectedTerm = getSelectedTerm(dealVersion);

        const offer = dealVersion.offer;
        const vehicle = getVehicle(dealVersion as any);

        return {
            key: index,
            version: offer.dealXgVersion,
            dealType: offerType,
            accepted: offer.accepted,
            createdOn: offer.createdOn, //date
            totalAllowance: getTotalAllowance(offer as any), // trade
            monthlyPayment: offer.monthlyPayment, // paymentDetails.payment
            sellingPrice: getOfferPriceByOfferType(dealVersion, offerType), // sellingPrice
            term: selectedTerm.months, // term
            downPayment: getDownPaymentByOfferType(dealVersion, offerType), // down or cashDownPayment
            frontEndProfit: offer.payment?.profit?.frontEndProfit,
            backEndProfit: offer.payment?.profit?.backEndProfit,
            amountFinanced: selectedTerm.totalFinanced,
            applicationName: offer.applicationName, // source
            offerSource: offer.offerSource, //modifiedBy
            stockNumber: vehicle.stockNumber, // stockNumber
            vin: vehicle.vin,
            inventoryType: vehicle.condition, // sellingVehicle.inventoryType or type
            annualMiles: getAnnualMiles(dealVersion), // annualMiles
            retailPrice: vehicle.retailPrice,
            color: vehicle.exteriorColor,
            year: vehicle.year,
            make: vehicle.make,
            model: vehicle.model,
            dealerUser: offer.dealerUser,
            imageUrl: vehicle.imageUrl
        };
    });
};

export const getDownloadAuditLogPayload = (state: StateTree): RequestPdfAuditLogPayload | null => {
    const downloadingVersion = getDownloadingVersion(state);
    if (!downloadingVersion) {
        return null;
    }
    const dealXgId = getDealXgId(state);
    const shopper = getShopperInfo(state);
    const coBuyer = getCoBuyerInfo(state);
    const dealerId = dealerSelectors.getDealerId(state);
    const shopperName = [shopper?.firstName, shopper?.middleName, shopper?.lastName, shopper?.suffix].filter(Boolean).join(' ');
    const coBuyerName = [coBuyer?.firstName, coBuyer?.middleName, coBuyer?.lastName, coBuyer?.suffix].filter(Boolean).join(' ');
    const transformedDealVersions = getTransformedDealVersions(state).filter((item) => item.version === downloadingVersion)[0];
    const dealChangeLog = getDealChangeLog(state).filter((item) => item.version === downloadingVersion)[0];
    if (!transformedDealVersions || !dealChangeLog) {
        return null;
    }
    const timeZone = dateUtils.getUserTimezone();
    return {
        changeLog: dealChangeLog.changeLog,
        dealInformation: {
            ...transformedDealVersions,
            shopperName,
            coBuyerName,
            dealerId,
            dealXgId,
            timeZone
        }
    };
};
